import { useField } from 'formik';
import debounce from 'lodash/debounce';
import React from 'react';

import { AutocompleteProps, AutocompleteRaw } from './Autocomplete.component';

export type AsyncAutocompleteRawProps<
  T,
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined
> = Omit<AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>, 'options' | 'inputValue' | 'setInputValue'> & {
  asyncOptions: (term: string) => Promise<T[]>;
};


export const useDebouncedValue = <T,>(value: T, ms: number) => {
  const [ inProgress, setInProgress ] = React.useState(false);
  const [ _value, _setValue ] = React.useState(value);
  const debouncedSetValue = React.useMemo(() => {
    return debounce((v: T) => {
      _setValue(v);
      setInProgress(false);
    }, ms);
  }, [ ms ]);

  React.useEffect(() => {
    return () => {
      setInProgress(false);
      debouncedSetValue.cancel();
    };
  }, [ debouncedSetValue ]);

  React.useEffect(() => {
    setInProgress(true);
    debouncedSetValue(value);
  }, [ value ]);

  return [
    _value,
    inProgress
  ] as const;
};


export const AsyncAutocompleteRaw = <
  T extends { id: string },
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined
>({ asyncOptions, ...props }: AsyncAutocompleteRawProps<T, TMultiple, TDisableClearable, TFreeSolo>) => {
  const [ inputValue, setInputValue ] = React.useState<string>('');
  const asyncOptionsRef = React.useRef(asyncOptions);
  const inputValueRef = React.useRef(inputValue);
  const [ loading, setLoading ] = React.useState(false);
  const [ options, setOptions ] = React.useState<T[]>([]);
  const delay = 300;

  asyncOptionsRef.current = asyncOptions;
  inputValueRef.current = inputValue;

  const handleTermChange = React.useMemo(() => debounce(async (term: string) => {
    try {
      if (term?.length < 1) {
        setOptions([]);
        setLoading(false);
        return;
      }
      const result = await asyncOptionsRef.current(term);

      if (term === inputValueRef.current) {
        setOptions(result);
        setLoading(false);
      }
    } catch (_err) {
      setLoading(false);
      console.error(_err);
    }
  }, delay), [ delay ]);

  React.useEffect(() => {
    setLoading(true);
    handleTermChange(inputValue);
  }, [ inputValue ]);

  return (
    <AutocompleteRaw
      filterOptions={opts => opts}
      {...props}
      options={options}
      loading={loading}
      inputValue={inputValue}
      setInputValue={setInputValue}
      setValue={props.setValue}
      data-testid="async-autocomplete"
    />
  );
};


export type AsyncAutocompleteProps<T> = Omit<AsyncAutocompleteRawProps<T>, 'value' | 'touched' | 'error'>;


export const AsyncAutocomplete = <T extends { id: string }>(props: AsyncAutocompleteProps<T>) => {
  const [ field, meta, helpers ] = useField<T | undefined>(props.name);

  const handleSetValue = React.useCallback((value?: T) => {
    if (props.setValue) props.setValue(value);
    helpers.setValue(value);
  }, [ helpers, props ]);

  return (
    <AsyncAutocompleteRaw
      {...props}
      value={field.value ?? null}
      setValue={handleSetValue}
      touched={meta.touched}
      error={meta.error}
    />
  );
};
